import { Opportunity } from "@entities";
import { LtrNumberRatio } from "@shared/frontend/components/LtrNumberRatio";
import AddIcon from "@mui/icons-material/Add";
import clsx from "clsx";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { opportunityRepo } from "@repositories";
import { TIME_DESCRIPTION_MAX_LENGTH, WEEK_DAYS } from "@shared/helpers/constants";
import { GROUP_INSTANCE_BULK_MAX } from "@shared/helpers/group-instance-schedule";
import { isValid } from "date-fns";
import {
  ADMIN_GROUP_ADD_INSTANCE,
  ADMIN_GROUP_ADD_INSTANCE_ERROR,
  ADMIN_GROUP_ADD_INSTANCE_TITLE,
  ADMIN_GROUP_BULK_EDIT,
  ADMIN_GROUP_BULK_EDIT_MAX,
  ADMIN_GROUP_BULK_GENERATE,
  ADMIN_GROUP_INSTANCES_EMPTY,
  ADMIN_GROUP_INSTANCES_HEADER,
  ADMIN_GROUP_INSTANCES_IN_GROUP,
  ADMIN_GROUP_INSTANCES_LOAD_ERROR,
  ADMIN_GROUP_NEW_INSTANCE_FOR,
  ADMIN_GROUP_SAVE_INSTANCE,
} from "@shared/helpers/opportunity-group-admin-copy";
import { hasValidCoordinates } from "@shared/helpers/geo";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormGoogle } from "../form-components/form-google-autocomplete/FormGoogle";
import { useAlert } from "../../contexts/AlertProvider";
import { REQUEST_DEFAULT_VALUES } from "../../contexts/RequestProvider";
import { formatGroupSlotSchedule } from "./format-group-slot-schedule";
import { GroupOpportunitySlotsTable } from "./GroupOpportunitySlotsTable";
import { GroupInstanceStatusBadge } from "./GroupInstanceStatusBadge";
import { GroupOpportunitySlotsBulkEdit } from "./GroupOpportunitySlotsBulkEdit";
import { GroupOpportunitySlotsBulkGenerate } from "./GroupOpportunitySlotsBulkGenerate";

import "./GroupOpportunitySlots.scss";

type DateEvent = (dayjs.Dayjs & { $d?: Date }) | null;

interface GroupOpportunitySlotsProps {
  parentId: string;
  parentTitle?: string;
  variant?: "default" | "sidebar";
  /** When true, hide create/bulk actions and treat instances as display-only. */
  readOnly?: boolean;
  /** When true, instance rows are display-only (no navigate to EventCard). */
  disableInstanceNavigation?: boolean;
  previewChildren?: Opportunity[];
  tourAnchors?: boolean;
}

type SlotDraft = {
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timeDescription: string;
  location: string;
  coordinates: { lat: number; lng: number };
  amount: number;
  isRemote: boolean;
};

function defaultSlotDraft(amount = 1): SlotDraft {
  const start = dayjs().add(1, "day").hour(9).minute(0).second(0).millisecond(0);
  return {
    hasTime: true,
    hasStartTime: true,
    startDate: start.toDate(),
    endDate: start.add(2, "hour").toDate(),
    timeDescription: "",
    location: "",
    coordinates: REQUEST_DEFAULT_VALUES.coordinates,
    amount,
    isRemote: false,
  };
}

const GroupOpportunitySlotsPreview: FC<{
  rows: Opportunity[];
  tourAnchors?: boolean;
}> = ({ rows, tourAnchors = false }) => (
  <Stack sx={{ width: "100%", gap: 2, p: 2, minHeight: 0, flex: 1 }} data-tour="cc-group-slots">
    <Typography variant="h6" color="primary">
      {ADMIN_GROUP_INSTANCES_IN_GROUP(rows.length)}
    </Typography>
    <GroupOpportunitySlotsTable
      rows={rows}
      tableId="tour-group-slots"
      tourAnchors={tourAnchors}
    />
  </Stack>
);

export const GroupOpportunitySlots: FC<GroupOpportunitySlotsProps> = (props) => {
  if (props.previewChildren) {
    return (
      <GroupOpportunitySlotsPreview
        rows={props.previewChildren}
        tourAnchors={props.tourAnchors}
      />
    );
  }
  return <GroupOpportunitySlotsLive {...props} />;
};

const GroupOpportunitySlotsLive: FC<GroupOpportunitySlotsProps> = ({
  parentId,
  parentTitle,
  variant = "default",
  readOnly = false,
  disableInstanceNavigation = false,
}) => {
  const isSidebar = variant === "sidebar";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity } = useAlert();
  const [addingSlot, setAddingSlot] = useState(false);
  const [bulkGenerateOpen, setBulkGenerateOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const blockNavigation = readOnly || disableInstanceNavigation;
  const [slotDraft, setSlotDraft] = useState<SlotDraft>(() =>
    defaultSlotDraft(),
  );

  const {
    data: children = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["group-opportunity-children", parentId],
    queryFn: () =>
      opportunityRepo.find({
        where: {
          parentOpportunityId: parentId,
          isDeleted: false,
        },
        orderBy: { startDate: "asc" },
        include: {
          associationRequested: { include: { user: true } },
          teams: { include: { team: true } },
          categories: true,
          populations: true,
        },
      }),
    enabled: !!parentId,
  });

  useEffect(() => {
    const valid = new Set(children.map((child) => child.id));
    setSelectedIds((prev) => prev.filter((id) => valid.has(id)));
  }, [children]);

  const selectedChildren = useMemo(
    () => children.filter((child) => selectedIds.includes(child.id)),
    [children, selectedIds],
  );
  const allSelected =
    children.length > 0 && selectedIds.length === children.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((selectedId) => selectedId !== id);
    });
  };

  const parentQuery = useQuery({
    queryKey: ["group-opportunity-parent", parentId],
    queryFn: () => opportunityRepo.findId(parentId),
    enabled: !!parentId,
  });

  const openAddForm = useCallback(() => {
    setSlotDraft(defaultSlotDraft(parentQuery.data?.amount ?? 1));
    setAddingSlot(true);
  }, [parentQuery.data?.amount]);

  const validateSlot = (): string | null => {
    if (!slotDraft.hasTime) {
      if (!slotDraft.timeDescription.trim()) {
        return "יש למלא תיאור זמן";
      }
    } else if (!slotDraft.hasStartTime) {
      if (!slotDraft.endDate) {
        return "יש למלא תאריך יעד";
      }
      if (slotDraft.endDate < new Date()) {
        return "תאריך היעד לא יכול להיות בעבר";
      }
    } else {
      if (!slotDraft.startDate || !slotDraft.endDate) {
        return "יש למלא תאריך התחלה וסיום";
      }
      if (slotDraft.endDate < slotDraft.startDate) {
        return "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
      }
      if (slotDraft.startDate < new Date()) {
        return "תאריך ההתחלה לא יכול להיות בעבר";
      }
    }
    if (!slotDraft.isRemote && !slotDraft.location.trim()) {
      return "יש למלא מיקום או לסמן התנדבות מרחוק";
    }
    if (
      !slotDraft.isRemote &&
      !hasValidCoordinates(slotDraft.coordinates)
    ) {
      return "יש לבחור מיקום מהרשימה או מהמפה";
    }
    if (slotDraft.amount < 1) {
      return "יש להזין לפחות מקום אחד";
    }
    return null;
  };

  const handleAddSlot = async () => {
    const validationError = validateSlot();
    if (validationError) {
      setSeverity("warning");
      setAlertText(validationError);
      return;
    }

    setIsCreating(true);
    try {
      const scheduleDay =
        slotDraft.hasTime && slotDraft.hasStartTime && slotDraft.startDate
          ? WEEK_DAYS[dayjs(slotDraft.startDate).day()]
          : slotDraft.hasTime && !slotDraft.hasStartTime && slotDraft.endDate
            ? WEEK_DAYS[dayjs(slotDraft.endDate).day()]
            : undefined;

      const res = await Opportunity.createGroupChildSlot(parentId, {
        hasTime: slotDraft.hasTime,
        hasStartTime: slotDraft.hasTime ? slotDraft.hasStartTime : true,
        startDate:
          slotDraft.hasTime && slotDraft.hasStartTime
            ? slotDraft.startDate
            : null,
        endDate: slotDraft.hasTime ? slotDraft.endDate : null,
        timeDescription: slotDraft.hasTime
          ? ""
          : slotDraft.timeDescription.trim(),
        location: slotDraft.isRemote ? "מרחוק" : slotDraft.location.trim(),
        coordinates: slotDraft.isRemote
          ? REQUEST_DEFAULT_VALUES.coordinates
          : slotDraft.coordinates,
        amount: slotDraft.amount,
        day:
          scheduleDay ??
          parentQuery.data?.day ??
          WEEK_DAYS[dayjs().day()],
        isRemote: slotDraft.isRemote,
      });
      setSeverity(res.success ? "success" : "error");
      setAlertText(res.message);
      if (res.success) {
        await refetch();
        void queryClient.invalidateQueries({ queryKey: ["event-card", parentId] });
        setAddingSlot(false);
      }
    } catch {
      setSeverity("error");
      setAlertText(ADMIN_GROUP_ADD_INSTANCE_ERROR);
    } finally {
      setIsCreating(false);
    }
  };

  const scheduleDateValue = slotDraft.hasTime
    ? slotDraft.hasStartTime
      ? slotDraft.startDate
      : slotDraft.endDate
    : null;

  const applyScheduleDate = (date: Date) => {
    setSlotDraft((prev) => {
      if (!prev.hasStartTime) {
        return { ...prev, endDate: date };
      }
      return { ...prev, startDate: date, endDate: date };
    });
  };

  const applyScheduleTime = (
    field: "startDate" | "endDate",
    picked: Date,
  ) => {
    setSlotDraft((prev) => {
      const base =
        field === "startDate"
          ? prev.startDate
            ? new Date(prev.startDate)
            : new Date()
          : prev.endDate
            ? new Date(prev.endDate)
            : prev.startDate
              ? new Date(prev.startDate)
              : new Date();
      base.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
      if (field === "startDate") {
        const next = { ...prev, startDate: base };
        if (next.endDate && next.endDate < base) {
          next.endDate = new Date(base.getTime() + 2 * 60 * 60 * 1000);
        }
        return next;
      }
      return { ...prev, endDate: base };
    });
  };

  const addForm = (
    <Stack
      className={clsx(
        "group-opportunity-slots-add",
        isSidebar && "group-opportunity-slots-add--sidebar",
      )}
      sx={isSidebar ? undefined : { p: 2, gap: 2 }}
    >
      <Typography
        className="group-opportunity-slots-add__title"
        variant={isSidebar ? "subtitle2" : "h6"}
      >
        {ADMIN_GROUP_ADD_INSTANCE_TITLE}
      </Typography>
      <Typography
        className="group-opportunity-slots-add__subtitle"
        variant="body2"
        color="text.secondary"
      >
        {ADMIN_GROUP_NEW_INSTANCE_FOR(parentTitle ?? "הקבוצה")}
        {!isSidebar &&
          " - התוכן הכללי מועתק מהקבוצה, התאריך והמיקום מוגדרים כאן."}
      </Typography>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={
          heIL.components.MuiLocalizationProvider.defaultProps.localeText
        }
        adapterLocale="he"
      >
        <Stack gap={1}>
          {slotDraft.hasTime && (
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={!slotDraft.hasStartTime}
                  onChange={(e) => {
                    const toUntil = e.target.checked;
                    setSlotDraft((prev) => ({
                      ...prev,
                      hasStartTime: !toUntil,
                      startDate: toUntil ? null : (prev.startDate ?? prev.endDate ?? new Date()),
                      endDate: prev.endDate ?? prev.startDate ?? new Date(),
                    }));
                  }}
                />
              }
              label={
                <Typography variant="body2" fontSize="0.82rem">
                  לביצוע עד
                </Typography>
              }
            />
          )}
          <FormControlLabel
            sx={{ m: 0, alignSelf: "flex-start" }}
            control={
              <Checkbox
                size="small"
                checked={!slotDraft.hasTime}
                onChange={(e) => {
                  const noTime = e.target.checked;
                  setSlotDraft((prev) => ({
                    ...prev,
                    hasTime: !noTime,
                    timeDescription: noTime ? prev.timeDescription : "",
                    startDate: noTime ? null : (prev.startDate ?? new Date()),
                    endDate: noTime ? null : (prev.endDate ?? new Date()),
                    hasStartTime: true,
                  }));
                }}
              />
            }
            label={
              <Typography variant="body2" fontSize="0.82rem">
                ללא זמן מוגדר
              </Typography>
            }
          />
          {slotDraft.hasTime ? (
            <Stack gap={1.25}>
              <DatePicker
                label={slotDraft.hasStartTime ? "בתאריך" : "עד תאריך"}
                format="DD/MM/YYYY"
                value={
                  scheduleDateValue ? dayjs(scheduleDateValue) : dayjs()
                }
                onChange={(event: DateEvent) => {
                  if (!event || !isValid(event.toDate())) return;
                  applyScheduleDate(new Date(event.$d ?? event.toDate()));
                }}
                slotProps={{
                  textField: { fullWidth: true, size: "small" },
                }}
              />
              {slotDraft.hasStartTime && (
                <TimePicker
                  label="משעה"
                  format="HH:mm"
                  timeSteps={{ minutes: 15 }}
                  value={
                    slotDraft.startDate
                      ? dayjs(slotDraft.startDate)
                      : dayjs().hour(9).minute(0)
                  }
                  onChange={(event: DateEvent) => {
                    if (!event || !isValid(event.toDate())) return;
                    applyScheduleTime(
                      "startDate",
                      new Date(event.$d ?? event.toDate()),
                    );
                  }}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              )}
              <TimePicker
                label={slotDraft.hasStartTime ? "עד שעה" : "בשעה"}
                format="HH:mm"
                timeSteps={{ minutes: 10 }}
                minTime={
                  slotDraft.hasStartTime && slotDraft.startDate
                    ? dayjs(slotDraft.startDate)
                    : undefined
                }
                value={
                  slotDraft.endDate
                    ? dayjs(slotDraft.endDate)
                    : dayjs().hour(11).minute(0)
                }
                onChange={(event: DateEvent) => {
                  if (!event || !isValid(event.toDate())) return;
                  applyScheduleTime(
                    "endDate",
                    new Date(event.$d ?? event.toDate()),
                  );
                }}
                slotProps={{
                  textField: { fullWidth: true, size: "small" },
                }}
              />
            </Stack>
          ) : (
            <TextField
              label="תיאור זמן"
              placeholder="למשל: פעם בשבוע, כל יום..."
              size="small"
              fullWidth
              value={slotDraft.timeDescription}
              onChange={(e) =>
                setSlotDraft((prev) => ({
                  ...prev,
                  timeDescription: e.target.value.slice(
                    0,
                    TIME_DESCRIPTION_MAX_LENGTH,
                  ),
                }))
              }
              inputProps={{ maxLength: TIME_DESCRIPTION_MAX_LENGTH }}
            />
          )}
        </Stack>
      </LocalizationProvider>
      <FormControlLabel
        sx={{ alignSelf: "flex-start", m: 0 }}
        control={
          <Checkbox
            size="small"
            checked={slotDraft.isRemote}
            onChange={(e) =>
              setSlotDraft((prev) => ({
                ...prev,
                isRemote: e.target.checked,
              }))
            }
          />
        }
        label={
          <Typography variant="body2" fontSize="0.82rem">
            התנדבות מרחוק
          </Typography>
        }
      />
      {!slotDraft.isRemote && (
        <FormGoogle
          label="מיקום"
          fullWidth
          size="small"
          placeValue={slotDraft.location}
          onLocationChange={({ locationName, latLng }) => {
            setSlotDraft((prev) => ({
              ...prev,
              location: locationName,
              coordinates: latLng,
            }));
          }}
        />
      )}
      <TextField
        label="מספר מתנדבים נדרש"
        type="number"
        size="small"
        inputProps={{ min: 1 }}
        value={slotDraft.amount}
        onChange={(e) =>
          setSlotDraft((prev) => ({
            ...prev,
            amount: Math.max(1, Number(e.target.value) || 1),
          }))
        }
        fullWidth={isSidebar}
        sx={isSidebar ? undefined : { maxWidth: 200 }}
      />
      <Stack
        className="group-opportunity-slots-add__actions"
        direction={isSidebar ? "column" : "row"}
        gap={1}
      >
        <Button
          variant="contained"
          fullWidth={isSidebar}
          disabled={isCreating}
          onClick={() => void handleAddSlot()}
        >
          {isCreating ? <CircularProgress size={22} /> : ADMIN_GROUP_SAVE_INSTANCE}
        </Button>
        <Button
          variant="outlined"
          fullWidth={isSidebar}
          disabled={isCreating}
          onClick={() => setAddingSlot(false)}
        >
          ביטול
        </Button>
      </Stack>
    </Stack>
  );

  if (addingSlot && !readOnly) {
    return addForm;
  }

  const slotsList = isLoading ? (
    <CircularProgress size={24} sx={{ alignSelf: "center" }} />
  ) : isError ? (
    <Stack gap={1}>
      <Typography className="group-opportunity-slots__error-text" color="error">
        {ADMIN_GROUP_INSTANCES_LOAD_ERROR}
      </Typography>
      <Button size="small" variant="outlined" onClick={() => void refetch()}>
        נסו שוב
      </Button>
    </Stack>
  ) : children.length === 0 ? (
    <Typography className="group-opportunity-slots__empty">
      {ADMIN_GROUP_INSTANCES_EMPTY}
    </Typography>
  ) : isSidebar ? (
    <div className="group-opportunity-slots__list">
      {children.map((child) => (
          <Box
            key={child.id}
            className="group-opportunity-slots__card"
            onClick={() => {
              if (blockNavigation) return;
              navigate(`/events/${child.id}`, { state: { selectedList: "" } });
            }}
            sx={
              blockNavigation
                ? { cursor: "default", textAlign: "inherit", opacity: 1 }
                : undefined
            }
          >
            <Stack direction="row" alignItems="flex-start" gap={0.5}>
              {!readOnly && (
                <Checkbox
                  size="small"
                  checked={selectedIds.includes(child.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) =>
                    toggleSelected(child.id, event.target.checked)
                  }
                  sx={{ p: 0.25, mt: -0.25 }}
                />
              )}
              <Stack flex={1} minWidth={0} gap={0.35}>
                <span className="group-opportunity-slots__card-date">
                  {formatGroupSlotSchedule(child)}
                </span>
                <span className="group-opportunity-slots__card-location">
                  {child.isRemote ? "מרחוק" : child.location || "-"}
                </span>
                <span className="group-opportunity-slots__card-meta">
                  <span className="group-opportunity-slots__card-participants">
                    <LtrNumberRatio
                      left={child.participantsCount ?? 0}
                      right={child.amount ?? 1}
                    />{" "}
                    משתתפים
                  </span>
                  <GroupInstanceStatusBadge child={child} />
                </span>
              </Stack>
            </Stack>
          </Box>
        ))}
    </div>
  ) : (
    <Table size="small" className="group-opportunity-slots-table">
      <TableHead>
        <TableRow>
          {!readOnly && (
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(event) => {
                  setSelectedIds(
                    event.target.checked ? children.map((child) => child.id) : [],
                  );
                }}
              />
            </TableCell>
          )}
          <TableCell>תאריך</TableCell>
          <TableCell>מיקום</TableCell>
          <TableCell>משתתפים</TableCell>
          <TableCell>סטטוס</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {children.map((child) => (
            <TableRow
              key={child.id}
              hover={!blockNavigation}
              selected={selectedIds.includes(child.id)}
              sx={{ cursor: blockNavigation ? "default" : "pointer" }}
              onClick={() => {
                if (blockNavigation) return;
                navigate(`/events/${child.id}`, {
                  state: { selectedList: "" },
                });
              }}
            >
              {!readOnly && (
                <TableCell
                  padding="checkbox"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(child.id)}
                    onChange={(event) =>
                      toggleSelected(child.id, event.target.checked)
                    }
                  />
                </TableCell>
              )}
              <TableCell>{formatGroupSlotSchedule(child)}</TableCell>
              <TableCell>
                {child.isRemote ? "מרחוק" : child.location || "-"}
              </TableCell>
              <TableCell>
                <LtrNumberRatio
                  left={child.participantsCount ?? 0}
                  right={child.amount ?? 1}
                />
              </TableCell>
              <TableCell>
                <GroupInstanceStatusBadge child={child} />
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  return (
    <Stack
      className={clsx(
        "group-opportunity-slots",
        isSidebar && "group-opportunity-slots--sidebar",
      )}
      sx={
        isSidebar
          ? undefined
          : { width: "100%", gap: 2, p: 2, minHeight: 0, flex: 1 }
      }
    >
      <div className="group-opportunity-slots__header">
        {isSidebar ? (
          <p className="group-opportunity-slots__title">
            {ADMIN_GROUP_INSTANCES_HEADER(children.length)}
          </p>
        ) : (
          <Typography variant="h6" color="primary">
            {ADMIN_GROUP_INSTANCES_IN_GROUP(children.length)}
          </Typography>
        )}
        <Stack direction="row" gap={1} flexWrap="wrap">
          {!readOnly && (
            <>
              {selectedIds.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  className="group-opportunity-slots__add-btn"
                  onClick={() => {
                    if (selectedIds.length > GROUP_INSTANCE_BULK_MAX) {
                      setSeverity("warning");
                      setAlertText(ADMIN_GROUP_BULK_EDIT_MAX(GROUP_INSTANCE_BULK_MAX));
                      return;
                    }
                    setBulkEditOpen(true);
                  }}
                >
                  {ADMIN_GROUP_BULK_EDIT(selectedIds.length)}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                className="group-opportunity-slots__add-btn"
                onClick={() => setBulkGenerateOpen(true)}
              >
                {ADMIN_GROUP_BULK_GENERATE}
              </Button>
              <Button
                variant="contained"
                size="small"
                className="group-opportunity-slots__add-btn"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openAddForm}
              >
                {ADMIN_GROUP_ADD_INSTANCE}
              </Button>
            </>
          )}
        </Stack>
      </div>
      {isSidebar ? (
        slotsList
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>{slotsList}</Box>
      )}
      <GroupOpportunitySlotsBulkGenerate
        open={bulkGenerateOpen}
        onClose={() => setBulkGenerateOpen(false)}
        parentId={parentId}
        parentTitle={parentTitle}
        defaultAmount={parentQuery.data?.amount ?? 1}
        existingChildren={children}
        onRefreshChildren={async () => {
          const result = await refetch();
          return result.data ?? children;
        }}
        onSuccess={async () => {
          await refetch();
          void queryClient.invalidateQueries({
            queryKey: ["group-opportunity-children", parentId],
          });
          void queryClient.invalidateQueries({ queryKey: ["event-card", parentId] });
        }}
      />
      <GroupOpportunitySlotsBulkEdit
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        parentId={parentId}
        selectedChildren={selectedChildren}
        onSuccess={async () => {
          setSelectedIds([]);
          await refetch();
          void queryClient.invalidateQueries({
            queryKey: ["group-opportunity-children", parentId],
          });
          void queryClient.invalidateQueries({ queryKey: ["event-card", parentId] });
        }}
      />
    </Stack>
  );
};
